import React, { useState } from 'react';
import { Eye, EyeOff, Key, Info, ChevronDown, ChevronRight } from 'lucide-react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'react-hot-toast';

interface SignUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenSignIn?: () => void;
}

export const SignUpModal: React.FC<SignUpModalProps> = ({ isOpen, onClose, onOpenSignIn }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
  const [showPasskeyInfo, setShowPasskeyInfo] = useState(false);
  const { signUp, signInWithPasskey } = useAuth();

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    try {
      await signUp(email, password);
      toast.success('Account created! Check your email to verify.');
      onClose();
      setEmail('');
      setPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      toast.error(error.message || 'Sign up failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasskeySignUp = async () => {
    setIsPasskeyLoading(true);
    try {
      await signInWithPasskey();
      toast.success('Account created with passkey!');
      onClose();
    } catch (error: any) {
      toast.error(error.message || 'Passkey sign up failed');
    } finally {
      setIsPasskeyLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setShowPasskeyInfo(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create Account" size="sm">
      <div className="space-y-6">
        {/* Passkey Sign Up */}
        <div className="text-center">
          <Button
            onClick={handlePasskeySignUp}
            disabled={isPasskeyLoading}
            className="w-full flex items-center justify-center space-x-2"
          >
            <Key className="h-4 w-4" />
            <span>{isPasskeyLoading ? 'Creating account...' : 'Create Account with Passkey'}</span>
          </Button>
          
          {/* Collapsible Passkey Explanation */}
          <div className="mt-2">
            <div className="flex justify-center">
              <button
                onClick={() => setShowPasskeyInfo(!showPasskeyInfo)}
                className="flex items-center justify-center space-x-1 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors"
              >
                <span>What is a passkey?</span>
                {showPasskeyInfo ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
              </button>
            </div>
            
            {showPasskeyInfo && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-left">
                <div className="flex items-start space-x-2">
                  <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-900 mb-1">What is a passkey?</h4>
                    <p className="text-xs text-blue-700">
                      A passkey is a secure way to sign in using your device's built-in security features like Face ID, Touch ID, or Windows Hello. 
                      It's faster and more secure than passwords - no need to remember or type anything!
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">or</span>
          </div>
        </div>

        {/* Email/Password Sign Up */}
        <form onSubmit={handleEmailSignUp} className="space-y-4">
          <div>
            <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <Input
              id="signup-email"
              type="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </div>

          <div>
            <label htmlFor="signup-password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <Input
                id="signup-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                placeholder="Create a password"
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-400" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="signup-confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <Input
                id="signup-confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-400" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full"
          >
            {isLoading ? 'Creating account...' : 'Create Account'}
          </Button>
        </form>

        {/* Sign In Link */}
        <div className="text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <button
              onClick={() => {
                onClose();
                onOpenSignIn?.();
              }}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Sign In
            </button>
          </p>
        </div>
      </div>
    </Modal>
  );
};

